import styles from './M_UserProfileCard.module.css';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import { User } from '@/types';
import cn from 'classnames';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';

interface M_UserProfileCardProps {
  user: User;
  isFollowing?: boolean;
  onFollow?: () => void;
  onCopyId?: () => void;
  className?: string;
}

export default function M_UserProfileCard({
  user,
  isFollowing = false,
  onFollow,
  onCopyId,
  className,
}: M_UserProfileCardProps) {
  const { language } = useLanguage();

  const handleFollow = () => {
    onFollow?.();
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(user.handle || user.id);
    onCopyId?.();
  };

  const formatDateOfBirth = (dateString?: string) => {
    if (!dateString)
      return getTranslation(language, 'userProfileCard.notSpecified');

    try {
      const date = new Date(dateString);
      const locale = language === 'ru' ? 'ru-RU' : 'en-US';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.content_top}>
        <div className={styles.header_wrapper}>
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={
                [user.personal.firstName, user.personal.lastName]
                  .filter(Boolean)
                  .join(' ') || user.handle
              }
              width={200}
              height={200}
              className={styles.avatar}
            />
          ) : (
            <A_UserAvatar width={200} height={200} />
          )}

          <div className={styles.user_info}>
            <h2 className={styles.user_name}>
              {[user.personal.firstName, user.personal.lastName]
                .filter(Boolean)
                .join(' ') || user.handle}
            </h2>

            <div className={styles.user_id_section}>
              <span className={cn(styles.user_id_value, 'text-p-7')}>
                @{user.handle || user.id}
              </span>
              <button
                className={styles.copy_button}
                onClick={handleCopyId}
                title={getTranslation(language, 'userProfileCard.copyId')}
              >
                <Q_Icon.CopyIcon width={16} height={16} fill='none' />
              </button>
            </div>
          </div>
        </div>
        <div className={styles.profile_metrics}>
          <div className={styles.metric_item}>
            <span className={cn('text-p-7', styles.metric_value)}>
              {user.metrics.followers}
            </span>
            <span className='text-p-7'>
              {getTranslation(language, 'userProfileCard.metrics.followers')}
            </span>
          </div>
          <div className={styles.metric_item}>
            <span className={cn('text-p-7', styles.metric_value)}>
              {user.metrics.following}
            </span>
            <span className='text-p-7'>
              {getTranslation(language, 'userProfileCard.metrics.following')}
            </span>
          </div>
          <div className={styles.metric_item}>
            <span className={cn('text-p-7', styles.metric_value)}>
              {user.metrics.coauthors}
            </span>
            <span className='text-p-7'>
              {getTranslation(language, 'userProfileCard.metrics.coauthors')}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.user_details}>
        <span className={cn(styles.detail_text, 'text-p-7')}>
          {user.location.label
            ? user.location.label.split(',')[0]
            : user.location.residencePlace?.city ||
              getTranslation(language, 'userProfileCard.notSpecified')}
        </span>
        <div className={styles.dot}></div>
        <span className={cn(styles.detail_text, 'text-p-7')}>
          {formatDateOfBirth(user.personal.dateOfBirth)}
        </span>
      </div>

      <div className={styles.follow_section}>
        <A_Button
          variant={isFollowing ? 'secondary' : 'primary'}
          size='large'
          onClick={handleFollow}
          icon={
            isFollowing ? (
              <Q_Icon.CheckmarkIcon width={20} height={20} fill='none' />
            ) : undefined
          }
        >
          {isFollowing
            ? getTranslation(language, 'userProfileCard.unfollow')
            : getTranslation(language, 'userProfileCard.follow')}
        </A_Button>
      </div>
    </div>
  );
}
